import { defineStore } from "pinia";
import { ref, computed, reactive, watch } from "vue";
import { useTheme } from "vuetify";
import { connectionStore } from "./ConnectionStore";
import { uiStore } from "./UiStore";
// import { useRouter } from "vue-router";
import { ls } from "@/plugin/secure";
import { useToast, TYPE } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

export const userStore = defineStore("user", () => {
  const supportLocales = ["ko_kr", "en_gn"];
  const defaultLocale = "ko_kr";
  const supportRoleTypes = [
    "SYSTEM",
    "ACCOUNT",
    "ENGINEER",
    "MAJOR",
    "MANAGER",
    "USER",
  ];
  const defaultRoleType = "SYSTEM";
  const defaultTheme = "light";
  const createDefaultUserInfo = () => ({
    userId: "demo_user",
    userName: "Demo User",
    roleType: defaultRoleType,
    locale: defaultLocale,
    theme: defaultTheme,
    landingpageType: "map_page",
    phonenumber: "",
  });

  const currentUser = ref(null);
  const userInfo = ref(createDefaultUserInfo());
  const router = useRouter();
  const theme = useTheme();
  const menus = ref([]);
  const { t } = useI18n();
  const i18n = useI18n();
  const portfolio = ref([]);
  const selectedPortfolio = ref([]);
  const useUiStore = uiStore();
  const toast = useToast();
  const useConnectionStore = connectionStore();

  const normalizeLocale = (rawLocale) => {
    if (typeof rawLocale === "string") {
      return supportLocales.includes(rawLocale) ? rawLocale : defaultLocale;
    }

    if (rawLocale && typeof rawLocale === "object") {
      const candidate =
        typeof rawLocale.code === "string"
          ? rawLocale.code
          : typeof rawLocale.locale === "string"
            ? rawLocale.locale
            : undefined;

      if (candidate && supportLocales.includes(candidate)) {
        return candidate;
      }
    }

    const currentLocale = i18n.locale.value;
    if (
      typeof currentLocale === "string" &&
      supportLocales.includes(currentLocale)
    ) {
      return currentLocale;
    }

    return defaultLocale;
  };
  const normalizeRoleType = (rawRoleType) => {
    if (typeof rawRoleType !== "string") {
      return defaultRoleType;
    }
    const roleType = rawRoleType.toUpperCase();
    return supportRoleTypes.includes(roleType) ? roleType : defaultRoleType;
  };
  const normalizeTheme = (rawTheme) => {
    if (typeof rawTheme !== "string") {
      return defaultTheme;
    }
    return rawTheme.toLowerCase() === "dark" ? "dark" : defaultTheme;
  };

  const setMenus = (params) => {
    menus.value = params;
  };
  const setUser = async (params) => {
    userInfo.value = {
      ...createDefaultUserInfo(),
      ...(params && typeof params === "object" ? params : {}),
      roleType: normalizeRoleType(params?.roleType),
      locale: normalizeLocale(params?.locale),
      theme: normalizeTheme(params?.theme),
    };
  };
  const setPortfolio = (params) => {
    if (params.selected) {
      portfolio.value.selected = params.selected;
    }
    if (params.portfolio) {
      portfolio.value.list = params.portfolio;
    }
  };
  const changeLocale = async (params) => {
    const locale = normalizeLocale(params);
    if (!userInfo.value || typeof userInfo.value !== "object") {
      userInfo.value = createDefaultUserInfo();
    }
    userInfo.value.locale = locale;
    i18n.locale.value = locale;
  };
  const getUserData = async () => {
    const { data, code } = await useConnectionStore.request({
      url: "/v2/auth/my",
      method: "post",
      queryparam: {},
    });

    if (code === 200) {
      const rawData =
        data && typeof data === "object" && !Array.isArray(data) ? data : {};
      const locale = normalizeLocale(rawData?.locale);
      const roleType = normalizeRoleType(rawData?.roleType);
      const themeName = normalizeTheme(rawData?.theme);
      i18n.locale.value = locale;
      theme.global.name.value = themeName;

      if (rawData.phonenumber) {
        rawData.phonenumber = rawData.phonenumber.replace(
          /^(.{3})(.*)(.{4})$/,
          "$1-$2-$3"
        );
      }
      await getPortfolioList();
      await setUser({
        ...rawData,
        locale,
        roleType,
        theme: themeName,
      });
    }
  };
  const getPortfolioList = async () => {
    const { because, code, data, message, result } =
      await useConnectionStore.request({
        url: "/v2/portfolio/current/list",
        method: "get",
      });
    if (code == 200) {
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        const returnPortfolio = {
          selected: list.find((item) => item.defaultYn === "y"),
          portfolio: list.sort((a, b) => {
            if (a.portfolioName === "lobby") return -1;
            if (b.portfolioName === "lobby") return 1;
            return a.portfolioName.localeCompare(b.portfolioName);
          }),
        };

        setPortfolio(returnPortfolio);
      } else {
        toast.warning(t("WARNING_PORTFOLIO_NO_RESULT"));
      }
    } else {
      toast.warning(t("ERROR_PORTFOLIO_API_ERROR"));
    }
  };

  const logoutUser = async () => {
    const { code } = await useConnectionStore.request({
      url: "/v2/auth/signout",
      method: "post",
      queryparam: {},
    });
    if (code == 200) {
      changeLocale("ko_kr");
      userInfo.value = createDefaultUserInfo();
      portfolio.value = [];
      ls.clear();
      router.push("/");
      toast.success(t("SUCCESS_LOGOUT"));
    } else {
      toast.error(t("ERROR_LOGOUT"));
    }
  };

  return {
    currentUser,
    userInfo,
    portfolio,
    menus,
    setMenus,
    changeLocale,
    setPortfolio,
    setUser,
    getUserData,
    logoutUser,
  };
});
